import SelectItemV2 from 'core-system/Dropdown/SelectItemV2'
import TextFieldV2 from 'core-system/TextFieldV2'
import pxToRem from 'core-system/utils/pxToRem'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import MapIntelligenceService from 'redux/mapIntelligence/mapIntelligenceService'
import { customAddressChoice } from 'redux/mapIntelligence/mapboxPlacesQuery'
import styled, { css } from 'styled-components'

interface AutoCompleteResults {
  type: string
  query: string[]
  features: any[]
  attribution: string
}

export interface Result {
  address: string
  coordinates: number[]
}

const Container = styled.div`
  width: 100%;
  position: relative;
`

const StyledTextField = styled(TextFieldV2)<{ isOpen: boolean }>`
  text-align: start;
  background-color: ${(props) => props.theme.palette.white};
  border-radius: ${pxToRem('4')};

  ${(props) =>
    props.isOpen &&
    css`
      border-radius: ${pxToRem('4')} ${pxToRem('4')} 0 0;
    `};
`

const StyledSelectItem = styled(SelectItemV2)`
  text-align: start;
`

const ResultsContainer = styled.div`
  position: absolute;
  z-index: ${(props) => props.theme.zIndex.dropdowns};
  box-shadow: ${(props) => props.theme.dropShadows.selected};
  padding: 0.5rem 0;
  border-radius: 0 0 ${pxToRem('4')} ${pxToRem('4')};
  background-color: ${(props) => props.theme.palette.white};
  width: 100%;
  top: ${(props) => props.theme.pxToRem(64)};
  border: 1px solid ${(props) => props.theme.palette.grey.grey10};
  > div {
    color: ${(props) => props.theme.palette.primary.pink500};
  }
`

const formatResults = (results: AutoCompleteResults) => {
  return results.features.map((location) => {
    return { address: location.place_name_en, coordinates: location.center }
  })
}

interface AddressAutocompleteProps {
  label: string
  hasError: boolean
  onActiveChange: (location: Result) => void
  customPlaceholder?: string
  allowCustomAddress?: boolean
}

const AddressAutocompleteV2 = React.memo((props: AddressAutocompleteProps) => {
  const {
    label,
    hasError,
    onActiveChange,
    customPlaceholder,
    allowCustomAddress = false,
  } = props
  const [address, setAddress] = useState('')
  const [results, setResults] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const addressRef = useRef(address)
  addressRef.current = address

  const containerRef = useRef(null)

  const clickOutside = useCallback(
    (e: MouseEvent) => {
      if (
        containerRef &&
        containerRef.current &&
        containerRef.current.contains(e.target)
      ) {
        return
      }
      setIsOpen(false)
    },
    [containerRef]
  )

  useEffect(() => {
    document.addEventListener('mousedown', clickOutside, false)
    return () => {
      document.removeEventListener('mousedown', clickOutside, false)
    }
  }, [clickOutside])

  const handleOnClick = (result: Result) => {
    setAddress(result.address)
    setIsOpen(false)
    onActiveChange(result)
  }

  const handleOnInputClick = () => {
    if (!isOpen && results) {
      setIsOpen(true)
    }
  }

  const renderResults = (results: Result[]) => {
    return results.map((result, idx) => {
      return (
        <StyledSelectItem
          active={true}
          key={idx}
          onClick={() => handleOnClick(result)}
        >
          {result.address}
        </StyledSelectItem>
      )
    })
  }

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.currentTarget.value

    setAddress(newValue)

    if (newValue.trim().length === 0) {
      setIsOpen(false)
      setResults(null)
      onActiveChange({ address: '', coordinates: [] })
      return
    }

    if (allowCustomAddress) {
      const typedAddress = customAddressChoice(newValue)
      if (typedAddress) {
        onActiveChange(typedAddress)
      }
    }

    if (newValue.length <= 4) {
      setResults(null)
      setIsOpen(false)
      return
    }

    MapIntelligenceService.getAddressAutocomplete(newValue)
      .then((res) => {
        if (addressRef.current !== newValue) {
          return
        }
        const matches = formatResults(res.data)
        setResults(matches)
        setIsOpen(matches.length > 0)
      })
      .catch((err) => {
        console.log(err)
        if (addressRef.current === newValue) {
          setResults(null)
          setIsOpen(false)
        }
      })
  }

  return (
    <Container ref={containerRef}>
      <StyledTextField
        label={label}
        onChange={(e) => handleOnChange(e)}
        onClick={handleOnInputClick}
        value={address}
        autoComplete='false'
        type='text'
        name='hidden'
        role='presentation'
        placeholder={customPlaceholder ? customPlaceholder : 'Street Address *'}
        isOpen={isOpen}
        invalid={hasError}
        helpText={
          allowCustomAddress
            ? 'Suggestions appear when we find a match'
            : 'Please select a full address'
        }
      />
      {isOpen && results?.length ? (
        <ResultsContainer>{renderResults(results)}</ResultsContainer>
      ) : null}
    </Container>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  AddressAutocompleteV2.displayName = 'AddressAutocompleteV2'
}

export default AddressAutocompleteV2
